import { Component, h, Prop, State, Element } from '@stencil/core';
import { extractImageProperties, formatProjectPeriod, fetchDefaultImage, constructProjectManchet } from '../../utils/utils';

interface CardImage {
  src: string;
  width?: number;
  height?: number;
  mdCrop?: Crop;
  smCrop?: Crop;
}

interface Crop {
  src: string;
  width: number;
  height: number;
  alias: string;
  srcset: string;
}
@Component({
  tag: 'wdf-content-card',
  shadow: false,
})
export class ContentCard {
  @Element() el: HTMLElement;
  @Prop() contentid: string;
  @Prop() header: string;
  @Prop() description: string;
  @Prop() route: string;
  @Prop() date: string;
  @Prop() type: string;
  @Prop() tags: string;
  @Prop() countries: string;
  @Prop() period: string;

  @Prop() imageDescription: string;

  // Image props
  @Prop() imageurl: string;
  @Prop() width: number;
  @Prop() height: number;
  @Prop() alttext: string;
  @Prop() cropstring: string;
  @Prop() mdthumbnail: string;
  @Prop() smthumbnail: string;

  @State() contentItemModel: any = null;

  private tagsContainer: HTMLElement;

  // Define a constant to hold the image properties object
  private contentProperties: {
    contentHeader?: string;
    contentDescription?: string;
    contentRoute?: string;
    contentType?: string;
    contentDate?: string;
    contentTags?: string;
    contentCountries?: string;
    contentProjectPeriod?: string;
    // image model
    image?: CardImage;
  } = {};

  async componentWillLoad() {
    if (!this.contentid) {
      this.setPropsFromProps();
    } else {
      try {
        await this.fetchContentItem();
      } catch (error) {
        console.error('An error occurred while fetching data:', error);
      }
    }
  }

  componentDidLoad() {
    // If the content is a project page, insert the project manchet
    if (this.contentProperties.contentType == 'projectPage') {
      this.tagsContainer.insertAdjacentHTML('beforeend', constructProjectManchet(this.contentProperties.contentCountries, this.contentProperties.contentProjectPeriod));
    }
  }

  fetchContentItem = async () => {
    try {
      const response = await fetch(`/umbraco/delivery/api/v1/content/item/${this.contentid}`);

      if (!response.ok) {
        console.error('Failed to fetch data from the API');
        return;
      }

      const data = await response.json();

      if (!data) {
        console.error('Invalid data structure in API response');
        return;
      }

      this.contentItemModel = data;

      this.setPropsFromApi();

      if (!data.properties.image) {
        const defaultImage = await fetchDefaultImage(this.contentItemModel.contentType);
        if (!defaultImage) return;
        const imageProperties = extractImageProperties(defaultImage);
        this.contentProperties.image = this.defineImageModel(imageProperties);
      }
    } catch (error) {
      console.error('An error occurred while fetching data:', error);
      throw error; // Rethrow the error to be caught in componentWillLoad
    }
  };

  // Function to format a date to the desired format
  formatDate = date => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-UK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  setPropsFromApi = () => {
    if (this.contentItemModel) {
      const { properties, route, contentType } = this.contentItemModel;
      // Update the contentProperties object
      this.contentProperties = {
        contentHeader: properties?.title,
        contentDescription: properties.description,
        contentRoute: route?.path,
        contentType,
        contentTags: properties.tagPicker?.map(tag => tag.name).join(', '),
        contentDate: contentType === 'newsPage' ? this.formatDate(properties?.date) : null,
        contentCountries: contentType === 'projectPage' ? properties?.countries : null,
        contentProjectPeriod: contentType === 'projectPage' ? properties?.period : null,
      };

      // Extract the "Thumbnail" crops and update contentProperties
      if (properties.image) {
        const image = properties.image[0];
        const imageProperties = extractImageProperties(image);

        const imageModel = this.defineImageModel(imageProperties);

        this.contentProperties.image = imageModel;
      }
    }
  };

  setPropsFromProps = () => {
    // Update the contentProperties object with the prop values
    this.contentProperties = {
      contentHeader: this.header,
      contentDescription: this.description,
      contentRoute: this.route,
      contentType: this.type,
      contentTags: this.tags,
      contentCountries: this.countries,
      contentDate: this.formatDate(this.date),
      contentProjectPeriod: formatProjectPeriod(this.period),
      // image model
      image: {
        src: this.imageurl,
        width: this.width,
        height: this.height,
        mdCrop: { src: this.mdthumbnail, width: this.width, height: this.height, alias: 'mdThumbnail', srcset: this.cropstring },
        smCrop: { src: this.smthumbnail, width: this.width, height: this.height, alias: 'smThumbnail', srcset: this.cropstring },
      },
    };
  };

  renderContentCategory() {
    // Get category baed on content type
    const contentTypeMap = {
      newsPage: 'News',
      storyPage: 'Story',
      projectPage: 'Project',
    };

    // type is either passed in as a prop or fetched from the API
    const type = this.contentProperties.contentType || this.contentItemModel?.contentType || '';

    // Return the category if it exists in the map
    return contentTypeMap[type] ? contentTypeMap[type] : null;
  }

  renderContentTags() {
    switch (this.contentProperties.contentType) {
      case 'storyPage':
        return this.renderStoryPageTags();
      case 'newsPage':
        return this.renderNewsPageDate();
      default:
        return null;
    }
  }

  renderStoryPageTags() {
    let tags = [];
    // If the properties come from the api, use the tagPicker property
    if (this.contentItemModel && this.contentItemModel.contentType === 'storyPage' && this.contentItemModel.properties.tagPicker) {
      const tagList = this.contentItemModel.properties.tagPicker;
      // Split by comma or semicolon
      const contentTags = this.contentProperties.contentTags.split(/[,;]/);
      tags =
        tagList?.length > 0
          ? tagList
              .map(tag => tag.name)
              .sort()
              .slice(0, 2)
          : contentTags;
    } else if (this.contentProperties.contentTags) {
      // If the properties come from the props, use the contentTags property
      tags = this.contentProperties.contentTags.split(', ');
    } else {
      return null;
    }

    const tagWithDivider = tags.map(tag => <span class="text-sm after:content-['\00a0│\00a0'] after:text-silver last:after:content-['']">{tag}</span>);

    return tagWithDivider;
  }

  renderNewsPageDate() {
    return this.contentProperties.contentDate ? <span class="text-sm after:content-['\00a0│\00a0'] after:text-silver last:after:content-['']">{this.contentProperties.contentDate}</span> : null;
  }

  private createImageHtml(cardImage: CardImage) {
    const imageMdCrop = cardImage.mdCrop.srcset;
    const imageSmCrop = cardImage.smCrop.srcset;
    const imageSrc = cardImage.mdCrop.src;
    const imageWidth = cardImage.mdCrop.width ? `${cardImage.width}` : '320';
    const imageHeight = cardImage.mdCrop.height ? `${cardImage.height}` : '260';
    // get computed sm media query width and use it as a breakpoint in the picture element
    const mediaQueryMinWidth = window.getComputedStyle(document.documentElement).getPropertyValue('--screen-sm') || '640px';
    const classes = 'w-full h-[160px] md:h-full object-cover';

    return (
      <picture class="lazyload">
        <source media={`(min-width: ${mediaQueryMinWidth})`} srcSet={imageMdCrop} class={classes} />
        <source srcSet={imageSmCrop} class={classes} />
        <img src={imageSrc} alt="" width={imageWidth} height={imageHeight} class={classes} />
      </picture>
    );
  }

  private defineImageModel(imageProperties: any) {
    const mdCrop = imageProperties.crops.filter(crop => crop.alias == 'mdThumbnail')[0];
    const smCrop = imageProperties.crops.filter(crop => crop.alias == 'smThumbnail')[0];

    return {
      src: imageProperties.contentImageUrl,
      width: mdCrop.width || null,
      height: mdCrop.height || null,
      mdCrop: { src: mdCrop.cropURL, width: mdCrop.width, height: mdCrop.height, alias: mdCrop.alias, srcset: mdCrop.cropString },
      smCrop: { src: smCrop.cropURL, width: smCrop.width, height: smCrop.height, alias: smCrop.alias, srcset: smCrop.cropString },
    };
  }

  render() {
    return (
      <li class="relative card lazyload dropshadow-animate flex flex-col justify-between h-full bg-white ">
        <a href={this.contentProperties.contentRoute} class="flex p-32 flex-col h-full justify-between items-start w-full gap-12">
          <div class="inner-card flex flex-col gap-16 w-full">
            <div class="text-14 font-bold text-gray-80">{this.renderContentCategory()}</div>

            <div class="default-image-box w-full overflow-hidden">
              {this.contentProperties.image ? this.createImageHtml(this.contentProperties.image) : <div class="object-cover w-full bg-silver-30 h-full"></div>}
            </div>

            {this.contentProperties.contentHeader ? <h2 class="content-container h4 font-bold text-gray">{this.contentProperties.contentHeader}</h2> : ''}
          </div>
          <div class="content-container flex flex-col gap-12 w-full">
            <div ref={el => (this.tagsContainer = el)} class="text-gray-80">
              {this.renderContentTags()}
            </div>
            <div class="btn btn-secondary btn-red self-end md:self-start">
              <svg class="h-16 w-42">
                <use class="arrow-right" xlinkHref="/assets/spritemap.svg#arrow-right" />
              </svg>
            </div>
          </div>
        </a>
      </li>
    );
  }
}
